/** @format */
import React, { useCallback, useEffect, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Logout from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import PersonIcon from "@mui/icons-material/Person";
import { Box, Button, Divider, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuthorizationHeader } from "../../../../constants";
import { isAuthorizedCustomerGuid } from "../../../utils/utils";
import { SubModAppImages, Website_URL } from "../../../constants/constants";
import Sidebar, { NavigationLinks } from "../Sidebar/Sidebar";
import "./Header.scss";

type PropType = { app: string };

export const pageTitle = (pathname: string) => {
  const pathParts = pathname.split("/");
  if (pathname === "/app/quotes/new/prospect") return "New Prospect Quote";
  if (pathname === "/app/quotes/new/customer") return "New Customer Quote";
  if (/^\/app\/quotes\/\d+$/.test(pathname)) return "Quote Detail";
  if (/^\/app\/quotes\/\d+\/copy$/.test(pathname)) return "Copy Quote";
  return pathParts.length === 3 ? pathParts[pathParts.length - 1] : `${pathParts[pathParts.length - 3]} Detail`;
};

const Header = ({ app }: PropType) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userProfile, setUserProfile] = useState({ name: "", email: "" });
  const [accountAnchor, setAccountAnchor] = useState<null | HTMLElement>(null);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);

  const logout = () => {
    axios.get(`${process.env.REACT_APP_API_BASE}/sso/v1/signout/${app}`, { headers: getAuthorizationHeader() })
      .finally(() => {
        ["display_name", "email", "expires_on", "session_guid"].forEach(key => sessionStorage.removeItem(key));
        navigate("./signin?e=signout");
      });
  };

  const profile = useCallback(() => {
    axios.get(`${process.env.REACT_APP_API_BASE}/sso/v1/profile/${app}`, { headers: getAuthorizationHeader() })
      .then(response => {
        if (app.toLowerCase() !== "portal"
          && !isAuthorizedCustomerGuid(response?.data?.customer_guid)) {
          navigate("./signin?e=Unauthorized+Account");
          sessionStorage.clear();
          return;
        }
        const fullName = [response?.data?.first_name, response?.data?.last_name].filter(Boolean).join(" ")
          || response?.data?.display_name || "";
        setUserProfile({ name: fullName, email: response?.data?.email || "" });
        sessionStorage.setItem("display_name", response.data.display_name);
        sessionStorage.setItem("email", response.data.email);
      })
      .catch(error => {
        if (error.response?.status === 401) {
          navigate("./signin?e=timeout");
          sessionStorage.clear();
        }
      });
  }, [app, navigate]);

  useEffect(() => { profile(); }, [profile]);
  useEffect(() => {
    if (sessionStorage.getItem("session_guid") === null) navigate("./signin?e=timeout");
  }, [navigate]);

  const accountOpen = Boolean(accountAnchor);
  const accountButton = (mobile = false) => <Tooltip title="Account settings">
    <Button className={mobile ? "profile-button-mob" : "profile-button"}
      onClick={event => setAccountAnchor(event.currentTarget)} size="small"
      aria-expanded={accountOpen ? "true" : undefined} aria-haspopup="true"
      aria-controls={accountOpen ? "account-menu" : undefined} variant="contained"
      endIcon={mobile ? undefined : <KeyboardArrowDownIcon />}>
      {mobile ? <PersonIcon /> : <span><strong>{userProfile.name}</strong><small>{userProfile.email}</small></span>}
    </Button>
  </Tooltip>;

  return <header className="authenticated-header">
    <Box className="authenticated-topbar">
      <IconButton className="mobile-navigation-button" aria-label="Open navigation"
        onClick={() => setMobileNavigationOpen(true)}><MenuIcon /></IconButton>
      <a href={Website_URL} className="topbar-logo">
        <img className="app-logo" alt="Managed Solution" src={SubModAppImages.logo} />
      </a>
      <nav className="desktop-navigation" aria-label="Primary navigation"><NavigationLinks horizontal /></nav>
      <div className="desktop-account">{accountButton()}</div>
      <div className="mobile-account">{accountButton(true)}</div>
    </Box>
    <Box className="page-title-row"><h1>{pageTitle(location.pathname)}</h1></Box>
    <Sidebar open={mobileNavigationOpen} onClose={() => setMobileNavigationOpen(false)} />
    <Menu anchorEl={accountAnchor} id="account-menu" open={accountOpen}
      onClose={() => setAccountAnchor(null)} onClick={() => setAccountAnchor(null)}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
      <Box className="account-menu-identity">
        <Typography component="strong">{userProfile.name}</Typography>
        <Typography component="small">{userProfile.email}</Typography>
      </Box>
      <Divider />
      <MenuItem onClick={logout}><ListItemIcon><Logout fontSize="small" /></ListItemIcon>Logout</MenuItem>
    </Menu>
  </header>;
};

export default Header;
